import {
  type AcademicTerm,
  classroomPerformance,
  compareStudentNumbers,
  type DailyRecord,
  type Student,
  studentNumberLabel,
} from "@classroom/domain";
import { Fragment, useState } from "react";
import { useTranslation } from "./i18n";
import type { RecordExportScope } from "./lib/localRecordExport";
import RecordExportButton from "./RecordExportButton";
import SelectMenu from "./SelectMenu";
import StudentPagination, { useStudentPagination } from "./StudentPagination";
import { ToolPageToolbar } from "./ToolPageHeader";

export default function ClassroomPerformance({
  classId,
  students,
  records,
  recordTerm,
  onExport,
}: {
  recordTerm?: AcademicTerm;
  onExport: (scope: RecordExportScope) => void;
  classId: string;
  students: Student[];
  records: DailyRecord[];
}) {
  const { t } = useTranslation("classroomPerformance");
  const [showDates, setShowDates] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("number");
  const [expanded, setExpanded] = useState<string>();
  const invalidRange = Boolean(startDate && endDate && startDate > endDate);
  const overview = classroomPerformance(classId, students, records, startDate, endDate);
  const visible = overview.students
    .filter((row) =>
      filter === "materials"
        ? row.missingMaterials > 0
        : filter === "noResponses"
          ? row.history.length > 0 && row.answers === 0
          : true,
    )
    .filter(({ student }) => `${studentNumberLabel(student)} ${student.name}`.includes(search.trim()))
    .sort(
      (a, b) =>
        (sort === "answers"
          ? b.answers - a.answers
          : sort === "materials"
            ? b.missingMaterials - a.missingMaterials
            : 0) || compareStudentNumbers(a.student, b.student),
    );
  const pagination = useStudentPagination(
    visible,
    JSON.stringify([classId, recordTerm?.id, startDate, endDate, filter, sort, search]),
  );
  const filters = [
    { id: "all", label: "students.filters.all", count: overview.students.length },
    {
      id: "materials",
      label: "students.filters.materials",
      count: overview.students.filter((row) => row.missingMaterials > 0).length,
    },
    {
      id: "noResponses",
      label: "students.filters.noResponses",
      count: overview.students.filter((row) => row.history.length > 0 && row.answers === 0).length,
    },
  ] as const;

  const output = (intent?: "print") =>
    onExport({
      range: { from: startDate, to: endDate },
      studentIds: visible.map(({ student }) => student.id),
      label: t("title"),
      intent,
    });
  return (
    <div className="grade-dashboard performance-dashboard">
      <h3 className="sr-only">{t("title")}</h3>
      <ToolPageToolbar
        scope={
          <button
            type="button"
            className="secondary-button"
            aria-expanded={showDates}
            aria-controls="performance-date-range"
            onClick={() => setShowDates(!showDates)}
          >
            {t("filters.customDates")}
          </button>
        }
        actions={<RecordExportButton onClick={() => output()} onPrint={() => output("print")} />}
      />
      {overview.days.length === 0 && (
        <section className="performance-empty">
          <h3>{t("empty.title")}</h3>
        </section>
      )}
      <section className="grade-card grade-students performance-students">
        <div className="performance-followup-filters" role="group" aria-label={t("students.filtersLabel")}>
          {filters.map((item) => (
            <button
              key={item.id}
              type="button"
              aria-pressed={filter === item.id}
              onClick={() => {
                setFilter(item.id);
                setExpanded(undefined);
              }}
            >
              {t(item.label)} <span>{item.count}</span>
            </button>
          ))}
        </div>
        <header>
          <div>
            <h3>{t("students.title")}</h3>
          </div>
          <div className="performance-student-filters">
            <label>
              <span className="sr-only">{t("students.searchLabel")}</span>
              <input
                aria-label={t("students.searchLabel")}
                placeholder={t("students.searchPlaceholder")}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </label>
            <SelectMenu
              label={t("students.sortLabel")}
              value={sort}
              onChange={setSort}
              options={[
                { value: "number", label: t("students.sort.number") },
                { value: "answers", label: t("students.sort.answers") },
                { value: "materials", label: t("students.sort.materials") },
              ]}
            />
          </div>
        </header>
        <fieldset
          id="performance-date-range"
          hidden={!showDates}
          className="performance-filters"
          aria-label={t("filters.region")}
        >
          <label>
            {t("filters.startDate")}
            <input
              min={recordTerm?.startDate}
              max={recordTerm?.endDate}
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
            />
          </label>
          <span aria-hidden="true">{t("filters.to")}</span>
          <label>
            {t("filters.endDate")}
            <input
              min={recordTerm?.startDate}
              max={recordTerm?.endDate}
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
            />
          </label>
          <button
            type="button"
            className="secondary-button"
            onClick={() => {
              setStartDate("");
              setEndDate("");
            }}
          >
            {t("filters.allDates")}
          </button>
        </fieldset>
        {invalidRange && <p role="alert">{t("filters.invalidRange")}</p>}
        {(startDate || endDate) && (
          <p className="performance-range-note">
            {t("filters.activeRange", {
              start: startDate || recordTerm?.startDate || t("filters.openStart"),
              end: endDate || recordTerm?.endDate || t("filters.openEnd"),
            })}
          </p>
        )}
        <div className="grade-table-scroll">
          <table aria-label={t("students.tableLabel")}>
            <thead>
              <tr>
                <th scope="col">{t("students.student")}</th>
                <th scope="col">{t("students.answers")}</th>
                <th scope="col">{t("students.missing")}</th>
              </tr>
            </thead>
            <tbody>
              {pagination.items.map((row) => (
                <Fragment key={row.student.id}>
                  <tr>
                    <td>
                      <button
                        type="button"
                        className="grade-student-button"
                        aria-label={t("students.view", {
                          number: studentNumberLabel(row.student),
                          name: row.student.name,
                        })}
                        aria-expanded={expanded === row.student.id}
                        aria-controls={`performance-${row.student.id}`}
                        onClick={() => setExpanded(expanded === row.student.id ? undefined : row.student.id)}
                      >
                        <span>{studentNumberLabel(row.student)}</span>
                        <b>{row.student.name}</b>
                        <i aria-hidden="true">{expanded === row.student.id ? "−" : "+"}</i>
                      </button>
                    </td>
                    <td>{row.history.length ? t("times", { count: row.answers }) : t("students.unrecorded")}</td>
                    <td className={row.missingMaterials ? "grade-followup" : ""}>
                      {row.history.length ? t("students.missingDays", { count: row.missingMaterials }) : "—"}
                    </td>
                  </tr>
                  {expanded === row.student.id && (
                    <tr>
                      <td colSpan={3}>
                        <section
                          id={`performance-${row.student.id}`}
                          className="performance-detail"
                          aria-label={t("students.details", {
                            number: studentNumberLabel(row.student),
                            name: row.student.name,
                          })}
                        >
                          {row.history.length ? (
                            <table aria-label={t("students.historyLabel")}>
                              <thead>
                                <tr>
                                  <th scope="col">{t("students.date")}</th>
                                  <th scope="col">{t("students.response")}</th>
                                  <th scope="col">{t("students.materials")}</th>
                                  <th scope="col">{t("students.note")}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {row.history.map((item) => (
                                  <tr key={item.date}>
                                    <td>{item.date}</td>
                                    <td>{t("times", { count: item.answers })}</td>
                                    <td>
                                      {item.missingMaterials
                                        ? t("students.missingMaterials")
                                        : t("students.noMissingMaterials")}
                                    </td>
                                    <td>{item.note || "—"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <p>{t("students.noHistory")}</p>
                          )}
                        </section>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <StudentPagination {...pagination} />
        {visible.length === 0 && <p className="grade-empty">{t("students.empty")}</p>}
      </section>
    </div>
  );
}
